import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import PlayCircleOutlineOutlinedIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { fetchVideos, deleteVideo, uploadVideo, fetchMulticamSets, MulticamSet } from '../graphqlService';
import { useAuth } from '../context/AuthContext';
import { brand } from '../theme';
import { formatBytes, pluralize } from '../utils/format';
import { getBackendErrorMessage } from '../utils/apolloError';
import UploadVideo from './UploadVideo';
import MulticamCard, { multicamCreatedTime } from './Multicam/MulticamCard';
import RecordingsList from './Library/RecordingsList';
import MulticamNewsBanner from './Multicam/MulticamNewsBanner';
import PageContainer from './ui/PageContainer';
import PageHeader from './ui/PageHeader';
import Section from './ui/Section';
import EmptyState from './ui/EmptyState';
import LoadingState from './ui/LoadingState';
import LibraryCard from './Library/LibraryCard';
import { quickEditPathFor } from './QuickEdits/openQuickEdit';
import {
  formatVideoDuration,
  joinMeta,
  LibrarySort,
  LibraryVideo,
  objectIdTime,
  SORT_LABELS,
  sortLibraryItems,
} from './Library/libraryFormat';

type Video = LibraryVideo;
type LibraryTab = 'videos' | 'multicam';

const VIDEO_SORTS: LibrarySort[] = ['newest', 'oldest', 'name', 'largest'];
const MULTICAM_SORTS: LibrarySort[] = ['newest', 'oldest', 'name'];

const DELETE_FALLBACK_ERROR = 'Failed to delete video. Please try again.';
const VIDEOS_LOAD_ERROR = "We couldn't load your videos. Check your connection and try again.";
const MULTICAM_LOAD_ERROR = "We couldn't load your multicam sets. Check your connection and try again.";

const DEMO_VIDEO = {
  url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
  name: 'Demo Video (Big Buck Bunny)',
  size: 0,
  duration: '0:00',
};

const videoMeta = (video: Video) => [
  formatVideoDuration(video.duration),
  video.size > 0 ? formatBytes(video.size) : null,
];

const tabLabel = (label: string, count?: number) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    {label}
    {count !== undefined && (
      <Box
        component="span"
        sx={{
          px: 0.75,
          minWidth: 20,
          height: 20,
          borderRadius: '10px',
          bgcolor: 'grey.200',
          color: 'text.secondary',
          fontSize: 11.5,
          fontWeight: 700,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {count}
      </Box>
    )}
  </Box>
);

interface VideoLibraryProps {
  /** Told about each video uploaded from the Library. */
  onVideoUploaded?: (newVideo: Video) => void;
}

const VideoLibrary: React.FC<VideoLibraryProps> = ({ onVideoUploaded }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // The tab lives in the URL (?tab=multicam) so the multicam set-up can come back to it.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: LibraryTab = searchParams.get('tab') === 'multicam' ? 'multicam' : 'videos';

  const [searchTerm, setSearchTerm] = useState('');
  const [videoSort, setVideoSort] = useState<LibrarySort>('newest');
  const [multicamSort, setMulticamSort] = useState<LibrarySort>('newest');

  // Videos
  const [videos, setVideos] = useState<Video[]>([]);
  const [videosLoading, setVideosLoading] = useState(true);
  const [videosError, setVideosError] = useState<string | null>(null);
  const videosRequest = useRef(0);

  // Multicam sets: null until the first load answers.
  const [multicamSets, setMulticamSets] = useState<MulticamSet[] | null>(null);
  const [multicamLoading, setMulticamLoading] = useState(false);
  const [multicamError, setMulticamError] = useState<string | null>(null);
  const multicamRequest = useRef(0);

  // Upload panel
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const uploadButtonRef = useRef<HTMLButtonElement>(null);
  const dropZoneRef = useRef<HTMLDivElement | null>(null);
  const focusDropZoneOnOpen = useRef(false);

  // Preview and delete (each dialog keeps its video through the closing animation)
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewVideo, setPreviewVideo] = useState<Video | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState<Video | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Shown above the grid; the load error (with Retry) is separate.
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteErrorRef = useRef<HTMLDivElement>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [demoCreating, setDemoCreating] = useState(false);
  // Kept through the closing animation, so the text doesn't vanish mid-fade.
  const [toast, setToast] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const showToast = (message: string) => setToast({ open: true, message });

  const loadVideos = useCallback(async () => {
    const request = ++videosRequest.current;
    setVideosLoading(true);
    setVideosError(null);
    try {
      const token = await user?.getIdToken(false);
      if (!token) return;
      const fetchedVideos: Video[] = await fetchVideos();
      if (request !== videosRequest.current) return;
      setVideos(fetchedVideos);
    } catch (loadError) {
      console.error('Failed to load videos:', loadError);
      if (request !== videosRequest.current) return;
      // Never presented as an empty library: the error replaces the grid.
      setVideosError(VIDEOS_LOAD_ERROR);
    } finally {
      if (request === videosRequest.current) setVideosLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  const loadMulticamSets = useCallback(async () => {
    if (!user) return;
    const request = ++multicamRequest.current;
    setMulticamLoading(true);
    setMulticamError(null);
    try {
      const sets = await fetchMulticamSets(100, 0);
      if (request !== multicamRequest.current) return;
      setMulticamSets(Array.isArray(sets) ? sets : []);
    } catch (err) {
      console.error('Failed to load multicam sets:', err);
      if (request !== multicamRequest.current) return;
      setMulticamError(MULTICAM_LOAD_ERROR);
    } finally {
      if (request === multicamRequest.current) setMulticamLoading(false);
    }
  }, [user]);

  // Loaded with the page (for the tab's count) and again whenever the tab
  // changes, so a set created or changed elsewhere shows up.
  const onMulticamTab = tab === 'multicam';
  useEffect(() => {
    loadMulticamSets();
  }, [loadMulticamSets, onMulticamTab]);

  const changeTab = (next: LibraryTab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'multicam') params.set('tab', 'multicam');
    else params.delete('tab');
    setSearchParams(params, { replace: true });
  };

  // Upload panel
  const openUpload = () => {
    if (uploadOpen) {
      dropZoneRef.current?.focus();
      return;
    }
    focusDropZoneOnOpen.current = true;
    setUploadOpen(true);
  };

  useEffect(() => {
    if (uploadOpen && focusDropZoneOnOpen.current) {
      focusDropZoneOnOpen.current = false;
      dropZoneRef.current?.focus();
    }
  }, [uploadOpen]);

  const closeUpload = () => {
    if (uploadBusy) return;
    setUploadOpen(false);
    uploadButtonRef.current?.focus();
  };

  const handleVideoUploaded = (newVideo: Video) => {
    setVideos((previous) => [
      { ...newVideo, isNew: true, addedAt: Date.now() },
      ...previous.filter((video) => video.id !== newVideo.id),
    ]);
    // "It now appears in your library below" has to be true.
    const term = searchTerm.trim().toLowerCase();
    if (term && !(newVideo.name || '').toLowerCase().includes(term)) setSearchTerm('');
    if (tab !== 'videos') changeTab('videos');
    // The list never loaded, so reload it rather than show just this video.
    if (videosError) loadVideos();
    onVideoUploaded?.(newVideo);
  };

  const handleTryDemoVideo = async () => {
    if (!user || demoCreating) return;

    setDemoCreating(true);
    setActionError(null);

    try {
      const token = await user.getIdToken(true);
      const created = await uploadVideo(DEMO_VIDEO.url, DEMO_VIDEO.name, DEMO_VIDEO.size, DEMO_VIDEO.duration, token);

      if (!created?.id) {
        throw new Error('Failed to create demo video');
      }

      const createdVideo: Video = {
        id: created.id,
        url: created.url || DEMO_VIDEO.url,
        name: created.name || DEMO_VIDEO.name,
        size: created.size || DEMO_VIDEO.size,
        duration: created.duration || DEMO_VIDEO.duration,
      };

      setVideos((previous) => [{ ...createdVideo, isNew: true, addedAt: Date.now() }, ...previous]);
      navigate(`/annotate/${createdVideo.id}`, { state: { videoUrl: createdVideo.url, videoName: createdVideo.name } });
    } catch (err) {
      console.error('Failed to create demo video:', err);
      setActionError("We couldn't add the demo video. Try again in a moment.");
    } finally {
      setDemoCreating(false);
    }
  };

  const [quickEditOpening, setQuickEditOpening] = useState<string | null>(null);
  /** Opens the video's quick edit (its newest, or a new one). */
  const handleQuickEdit = async (video: Video) => {
    if (quickEditOpening) return;
    setQuickEditOpening(video.id);
    try {
      navigate(await quickEditPathFor({ videoId: video.id }));
    } catch (error) {
      setActionError(getBackendErrorMessage(error, 'The quick edit couldn’t be opened. Try again.'));
    } finally {
      setQuickEditOpening(null);
    }
  };

  const handlePreview = (video: Video) => {
    setPreviewVideo(video);
    setPreviewOpen(true);
  };

  const handleDeleteClick = (video: Video) => {
    setDeleteError(null);
    setVideoToDelete(video);
    setDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    if (deleting) return;
    setDeleteOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!videoToDelete || !user) return;

    setDeleting(true);
    setDeleteError(null);
    try {
      const token = await user.getIdToken(true);
      const result = await deleteVideo(videoToDelete.id, token);

      if (result?.success) {
        const deletedId = videoToDelete.id;
        setVideos((previous) => previous.filter((video) => video.id !== deletedId));
        setDeleteOpen(false);
        showToast('Video deleted');
      } else {
        // Close the dialog so the reason is visible on the page.
        setDeleteOpen(false);
        setDeleteError(result?.message || DELETE_FALLBACK_ERROR);
      }
    } catch (error: any) {
      console.error('Error deleting video:', error);
      // The backend refuses to delete a video that a project or multicam set
      // uses and says which; show that. Network failures keep the generic text.
      setDeleteOpen(false);
      setDeleteError(getBackendErrorMessage(error, DELETE_FALLBACK_ERROR));
    } finally {
      setDeleting(false);
    }
  };

  // The card is usually far down the page; bring the reason into view.
  useEffect(() => {
    if (deleteError) deleteErrorRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [deleteError]);

  const handleMulticamDeleted = (deletedId: string) => {
    setMulticamSets((previous) => (previous ? previous.filter((set) => set.id !== deletedId) : previous));
    showToast('Multicam set deleted');
  };

  const term = searchTerm.trim().toLowerCase();

  const visibleVideos = useMemo(
    () =>
      sortLibraryItems(
        videos.filter((video) => (video.name || '').toLowerCase().includes(term)),
        videoSort,
        {
          time: (video) => objectIdTime(video.id) ?? video.addedAt ?? null,
          name: (video) => video.name || '',
          size: (video) => video.size || 0,
        }
      ),
    [videos, term, videoSort]
  );

  const visibleSets = useMemo(
    () =>
      sortLibraryItems(
        (multicamSets ?? []).filter((set) => (set.name || '').toLowerCase().includes(term)),
        multicamSort,
        {
          time: (set) => multicamCreatedTime(set.createdAt),
          name: (set) => set.name || '',
        }
      ),
    [multicamSets, term, multicamSort]
  );

  const videoCount = videosLoading || videosError ? undefined : videos.length;
  const multicamCount = multicamSets && !multicamError ? multicamSets.length : undefined;
  const hasItems = tab === 'videos' ? !videosError && videos.length > 0 : !multicamError && (multicamSets?.length ?? 0) > 0;

  const noMatches = (total: number, noun: string, plural: string) => (
    <EmptyState
      compact
      icon={<SearchOutlinedIcon />}
      title={`No ${plural} match “${searchTerm.trim()}”`}
      description={`${pluralize(total, noun, plural)} in your library.`}
      secondaryActionLabel="Clear search"
      onSecondaryAction={() => setSearchTerm('')}
    />
  );

  const renderVideos = () => {
    if (videosError) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={loadVideos}>
              Retry
            </Button>
          }
        >
          {videosError}
        </Alert>
      );
    }
    if (videosLoading && videos.length === 0) {
      return <LoadingState variant="cards" count={8} label="Loading videos" />;
    }
    if (videos.length === 0) {
      return (
        <EmptyState
          icon={<VideoLibraryOutlinedIcon />}
          title="No videos yet"
          description="Upload the footage you want edited. You'll annotate it, then send it to an editor in a project."
          actionLabel="Upload a video"
          onAction={openUpload}
          secondaryActionLabel={demoCreating ? 'Adding the demo video…' : 'Try a demo video'}
          onSecondaryAction={handleTryDemoVideo}
        />
      );
    }
    if (visibleVideos.length === 0) return noMatches(videos.length, 'video', 'videos');
    return (
      <Grid container spacing={2.5}>
        {visibleVideos.map((video) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={video.id}>
            <LibraryCard
              title={video.name || 'Untitled video'}
              meta={videoMeta(video)}
              posterSrc={video.url}
              isNew={video.isNew}
              openLabel="Annotate"
              openTo={`/annotate/${video.id}`}
              openState={{ videoUrl: video.url, videoName: video.name }}
              actions={[
                {
                  label: 'Preview',
                  icon: <PlayCircleOutlineOutlinedIcon fontSize="small" />,
                  onClick: () => handlePreview(video),
                  testId: `preview-video-${video.id}`,
                },
                {
                  label: quickEditOpening === video.id ? 'Opening…' : 'Quick edit',
                  icon: <AutoFixHighOutlinedIcon fontSize="small" />,
                  onClick: () => handleQuickEdit(video),
                  testId: `quick-edit-video-${video.id}`,
                },
                {
                  label: 'Delete',
                  icon: <DeleteOutlineOutlinedIcon fontSize="small" />,
                  onClick: () => handleDeleteClick(video),
                  destructive: true,
                  testId: `delete-video-${video.id}`,
                },
              ]}
              testId={`video-card-${video.id}`}
              openTestId={`annotate-video-${video.id}`}
              menuTestId={`video-actions-${video.id}`}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderMulticamSets = () => {
    if (multicamError) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={loadMulticamSets} disabled={multicamLoading}>
              Retry
            </Button>
          }
        >
          {multicamError}
        </Alert>
      );
    }
    if (multicamSets === null) {
      return <LoadingState variant="cards" count={4} label="Loading multicam sets" />;
    }
    if (multicamSets.length === 0) {
      return (
        <EmptyState
          icon={<VideocamOutlinedIcon />}
          title="No multicam sets yet"
          description="Combine up to six camera angles of the same recording, lined up by their sound or by picture, and cut between them. Or join separate clips to play one after another. Your editor gets them with a transcript and a rough cut."
          actionLabel="New multicam set"
          onAction={() => navigate('/multicam/new')}
        />
      );
    }
    if (visibleSets.length === 0) return noMatches(multicamSets.length, 'multicam set', 'multicam sets');
    return (
      <Grid container spacing={2.5}>
        {visibleSets.map((multicamSet) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={multicamSet.id}>
            <MulticamCard multicamSet={multicamSet} onDeleted={() => handleMulticamDeleted(multicamSet.id)} />
          </Grid>
        ))}
      </Grid>
    );
  };

  const sortValue = tab === 'videos' ? videoSort : multicamSort;
  const sortOptions = tab === 'videos' ? VIDEO_SORTS : MULTICAM_SORTS;
  const searchLabel = tab === 'videos' ? 'Search videos' : 'Search multicam sets';

  return (
    <PageContainer>
      <PageHeader
        title="Library"
        subtitle="Your videos and multicam sets, ready to annotate and send to an editor."
        action={
          <>
            <Button component={RouterLink} to="/multicam/new" variant="outlined" startIcon={<AddOutlinedIcon />}>
              New multicam set
            </Button>
            <Button
              ref={uploadButtonRef}
              variant="contained"
              startIcon={<FileUploadOutlinedIcon />}
              onClick={openUpload}
              aria-expanded={uploadOpen}
              aria-controls={uploadOpen ? 'library-upload' : undefined}
            >
              Upload video
            </Button>
          </>
        }
      />

      <MulticamNewsBanner onCreate={() => navigate('/multicam/new')} />

      {uploadOpen && (
        // The close button sits in the corner rather than in Section's action
        // slot, which stacks under the title on phones.
        <Box sx={{ position: 'relative', mb: 3 }}>
          <Section id="library-upload" title="Upload video">
            <UploadVideo
              embedded
              onVideoUploaded={handleVideoUploaded}
              onUploadingChange={setUploadBusy}
              dropZoneRef={dropZoneRef}
            />
          </Section>
          <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
            <Tooltip title={uploadBusy ? 'Cancel the upload to close this' : ''}>
              <span>
                <IconButton aria-label="Close upload" size="small" onClick={closeUpload} disabled={uploadBusy}>
                  <CloseOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      )}

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      {deleteError && (
        <Alert
          ref={deleteErrorRef}
          severity="error"
          sx={{ mb: 2, scrollMarginTop: 88 }}
          onClose={() => setDeleteError(null)}
          data-testid="delete-video-error"
        >
          {deleteError}
        </Alert>
      )}

      <Box sx={{ borderBottom: `1px solid ${brand.border}`, mb: 2.5 }}>
        <Tabs value={tab} onChange={(_event, value: LibraryTab) => changeTab(value)} aria-label="Library sections">
          <Tab value="videos" label={tabLabel('Videos', videoCount)} id="library-tab-videos" aria-controls="library-tabpanel" />
          <Tab
            value="multicam"
            label={tabLabel('Multicam sets', multicamCount)}
            id="library-tab-multicam"
            aria-controls="library-tabpanel"
          />
        </Tabs>
      </Box>

      <Box role="tabpanel" id="library-tabpanel" aria-labelledby={`library-tab-${tab}`}>
        {hasItems && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
            <TextField
              type="search"
              size="small"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={searchLabel}
              inputProps={{ 'aria-label': searchLabel }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: '1 1 240px', maxWidth: { sm: 380 } }}
            />
            <TextField
              select
              size="small"
              label="Sort"
              value={sortValue}
              onChange={(event) => {
                const value = event.target.value as LibrarySort;
                if (tab === 'videos') setVideoSort(value);
                else setMulticamSort(value);
              }}
              sx={{ minWidth: 172 }}
            >
              {sortOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {SORT_LABELS[option]}
                </MenuItem>
              ))}
            </TextField>
            {tab === 'multicam' && (
              <Tooltip title="Refresh">
                <span>
                  <IconButton aria-label="Refresh multicam sets" onClick={loadMulticamSets} disabled={multicamLoading}>
                    <RefreshOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}

        {tab === 'videos' ? renderVideos() : (
          <>
            {renderMulticamSets()}
            {/* Separate audio recordings live outside the video grid, but use storage. */}
            <RecordingsList />
          </>
        )}
      </Box>

      {/* Preview */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        TransitionProps={{ onExited: () => setPreviewVideo(null) }}
        maxWidth="md"
        fullWidth
        aria-labelledby="video-preview-title"
      >
        <DialogTitle id="video-preview-title" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
          {previewVideo?.name}
        </DialogTitle>
        <DialogContent>
          {previewVideo && (
            <>
              <Box sx={{ bgcolor: brand.ink, borderRadius: 2, overflow: 'hidden' }}>
                {/* Captions are not available for creator uploads. */}
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                  data-testid="video-preview-player"
                  src={previewVideo.url}
                  controls
                  playsInline
                  preload="metadata"
                  style={{ width: '100%', maxHeight: '60vh', display: 'block' }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, fontVariantNumeric: 'tabular-nums' }}>
                {joinMeta(videoMeta(previewVideo)) || '—'}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          {previewVideo && (
            <Button
              variant="contained"
              component={RouterLink}
              to={`/annotate/${previewVideo.id}`}
              state={{ videoUrl: previewVideo.url, videoName: previewVideo.name }}
            >
              Annotate
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete: one confirmation that names the video */}
      <Dialog
        open={deleteOpen}
        onClose={handleCancelDelete}
        TransitionProps={{ onExited: () => setVideoToDelete(null) }}
        maxWidth="xs"
        fullWidth
        aria-labelledby="delete-video-title"
        aria-describedby="delete-video-description"
      >
        <DialogTitle id="delete-video-title" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
          Delete “{videoToDelete?.name}”?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-video-description" variant="body2">
            This permanently deletes the video and its notes. It can't be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" onClick={handleCancelDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete video'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={(_event, reason) => {
          if (reason !== 'clickaway') setToast((current) => ({ ...current, open: false }));
        }}
        message={toast.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </PageContainer>
  );
};

export default VideoLibrary;
